import { DEFAULT_ANIMATION_SPEED } from '@src/config/imageSettings';
import { TimelineMode } from '@src/editor/commands/TimelineMode';
import { Timeline } from '@src/editor/models/Timeline';
import { AspectRatio } from '@src/types.ts/video';
import applyImageOptimizations from '@src/utility/applyImageOptimizations';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { v4 as uuid } from 'uuid';

import ChatGPTService from '../../services/ChatGPTService';
import { MediaCard, SidebarOption } from '../../types.ts/general';
import { getImageWithBlackFrameElements } from '../../utility/elements';

import type { IVideoEditor } from '../../editor/interface/IVideoEditor';
import type { ImageKey } from '../../types.ts/general';
import type { PunchListItem, StoryDTO } from '../../types.ts/story';
import type VideoCreatorStore from '../VideoCreatorStore'; // Adjust import path as needed
import type { EditorStore } from '@src/editor';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

class PunchListManager {
  videoCreator: VideoCreatorStore;
  punchListData: PunchListItem[] | null = null;
  addedPunchListItemId: string | null = null;
  sidebarOptions = SidebarOption.media;
  mediaSubMenu = MediaCard.photo;

  private datoClientStore: DatoClientStore;
  private editorStore: EditorStore;
  private isSyncingFromEditor = false; // Prevent circular sync

  constructor(
    store: VideoCreatorStore,
    datoClientStore: DatoClientStore,
    editorStore: EditorStore,
  ) {
    // TODO: DatoClientStore
    makeAutoObservable(this, {
      videoCreator: false,
    });
    this.videoCreator = store;
    this.datoClientStore = datoClientStore;
    this.editorStore = editorStore;
  }

  length = (): number => {
    if (this.punchListData === null || !Array.isArray(this.punchListData))
      return 0;
    return this.punchListData.length;
  };

  /**
   * Sync punch list data from editor's punch list models.
   * Called after reconciliation/recalculation to update observable punchListData.
   * Inserted via src/di/container.ts
   */
  syncFromEditor(editor: IVideoEditor): void {
    if (!editor) {
      return;
    }

    const trackManager = editor.getTrackManager();
    const punchListItems = trackManager?.punchListItems;
    if (!punchListItems || punchListItems.size === 0) {
      // Clear punch list if no items
      runInAction(() => {
        this.isSyncingFromEditor = true;
        try {
          this.punchListData = [];
        } finally {
          this.isSyncingFromEditor = false;
        }
      });
      return;
    }

    // Extract punch list items from models via toJSON()
    const updatedItems: PunchListItem[] = [];
    for (const [, model] of punchListItems.entries()) {
      updatedItems.push(model.toJSON());
    }

    // Update observable punchListData
    runInAction(() => {
      this.isSyncingFromEditor = true;
      try {
        this.punchListData = updatedItems;
      } finally {
        this.isSyncingFromEditor = false;
      }
    });
  }

  set punchListItems(items: PunchListItem[]) {
    this.punchListData = items;
  }

  get punchListItems(): PunchListItem[] {
    if (this.length() === 0 || this.punchListData === null) return [];
    return [...this.punchListData];
  }

  // for detecting changes
  get punchListSignature(): string {
    return this.punchListItems
      .map(item => {
        const id = item.id;
        const pos = item.transcriptPosition;
        const assigned = item.artifactSrc ? 1 : 0;
        return `${id}|${pos.startIndex}|${pos.endIndex}|${assigned}`;
      })
      .join(',');
  }

  findPunchListItemByTranscriptPosition(
    transcriptIndex: number,
  ): PunchListItem | undefined {
    return this.punchListData?.find(
      item =>
        transcriptIndex >= item.transcriptPosition.startIndex &&
        transcriptIndex <= item.transcriptPosition.endIndex,
    );
  }

  isLastPhotoHighlight(
    transcriptIndex: number,
    punchListItem: PunchListItem,
  ): boolean {
    return punchListItem.transcriptPosition.endIndex === transcriptIndex;
  }

  getPhotoHighlightViewData(globalIndex: number): {
    punchListItem: PunchListItem | undefined;
    lastPhotoHighlightActive: boolean;
    lastPhotoHighlightElementId: string | undefined;
  } {
    const punchListItem =
      this.findPunchListItemByTranscriptPosition(globalIndex);
    // Check for last photo highlight element
    let lastPhotoHighlightActive = false;
    let lastPhotoHighlightElementId: string | undefined = undefined;
    if (
      punchListItem &&
      this.isLastPhotoHighlight(globalIndex, punchListItem)
    ) {
      lastPhotoHighlightElementId = punchListItem.id;
      lastPhotoHighlightActive = !!punchListItem.artifactSrc;
    }
    return {
      punchListItem,
      lastPhotoHighlightElementId,
      lastPhotoHighlightActive,
    };
  }

  findPunchListItemById(id: string): PunchListItem | undefined {
    return this.punchListData?.find(item => item.id === id);
  }

  updatePunchListItem(id: string, changes: Partial<PunchListItem>): void {
    const item = this.findPunchListItemById(id);
    if (item) {
      Object.assign(item, changes);
    }
  }

  removeIfPunchListItem(id: string): void {
    if (this.findPunchListItemById(id)) {
      this.removePunchListItem(id);
    }
  }

  // Mutator: Remove a punch list item by ID
  removePunchListItem(id: string): void {
    this.removePunchListItems([id]);
  }

  removePunchListItems(ids: string[]): void {
    this.punchListData =
      this.punchListData?.filter(i => i.id && !ids.includes(i.id)) || null;
  }

  private getDefaultMediaElementAnimations() {
    return [
      {
        start_scale: '100%',
        end_scale: DEFAULT_ANIMATION_SPEED,
        x_anchor: '50%',
        y_anchor: '50%',
        fade: false,
        scope: 'element',
        easing: 'linear',
        type: 'scale',
        arbor_subType: 'zoomIn',
      },
    ];
  }

  addPhotoToPunchList(
    text: string,
    photoUrl: string,
    description: string,
    type: ImageKey,
    startIndex: number,
    endIndex: number,
    aspectRatio: number,
  ) {
    const punchListId = uuid();
    try {
      this.addedPunchListItemId = punchListId;

      const newPunchListData: PunchListItem = {
        id: punchListId,
        type,
        sub_type: 'manual',
        description,
        line: text,
        artifactSrc: photoUrl,
        artifactAspectRatio: aspectRatio,
        transcriptPosition: {
          startIndex,
          endIndex,
        },
        metadata: {},
        evocative: '',
        dalleImages: [],
        elementId: punchListId,
        dallePrompt: '',
        stockKeywords: '',
      };

      let punchListData = this.insertPunchListAtIndex([newPunchListData]);
      this.punchListData = punchListData;
      return newPunchListData;
    } catch (error) {
      console.log('An error occurred: ', error);
      throw error;
    } finally {
      this.addedPunchListItemId = null;
    }
  }

  async addPunchListItemToTimeline(
    punchListItem: PunchListItem,
    duration: number,
    startTime: number,
    endTime: number,
    track: number,
  ) {
    await this.addPunchListItemViaHeadlessEditor(
      punchListItem,
      duration,
      startTime,
      track,
    );

    punchListItem.startTime = startTime;
    punchListItem.endTime = endTime;
    punchListItem.duration = duration;
    punchListItem.elementId = punchListItem.id ?? '';
  }

  /**
   * Adds punch list item using the headless editor commands
   */
  private async addPunchListItemViaHeadlessEditor(
    punchListItem: PunchListItem,
    duration: number,
    startTime: number,
    track: number,
  ) {
    const isPortrait =
      !!punchListItem.artifactAspectRatio &&
      punchListItem.artifactAspectRatio < 1;
    const videoAspectRatio =
      this.videoCreator.currentVideo?.aspectRatio ||
      this.videoCreator.getDefaultAspectRatio();

    const source = applyImageOptimizations(punchListItem.artifactSrc);
    const editorStore = this.videoCreator.rootStore?.editorStore;

    if (isPortrait && videoAspectRatio === AspectRatio.AR_16_9) {
      // Create composition element for portrait images in 16:9 videos
      const compositionElement = Timeline.createElement('composition', {
        id: punchListItem.id,
        time: startTime,
        duration,
        elements: getImageWithBlackFrameElements({ source }).map(el =>
          Timeline.createElement(el.type as string, el),
        ),
        animations: this.getDefaultMediaElementAnimations(),
      });

      const command = editorStore!.editor.commandAddMediaElement(
        compositionElement,
        `${track}`,
        TimelineMode.OVERLAP, // Use OVERLAP mode to allow adjacent photos on same track
      );

      if (!command) {
        throw new Error(
          'Failed to create composition element via headless editor',
        );
      }
    } else {
      // Create image element
      const imageElement = Timeline.createElement('image', {
        id: punchListItem.id,
        source,
        time: startTime,
        duration,
        autoplay: true,
        fit: 'cover',
        smart_crop: true,
        animations: this.getDefaultMediaElementAnimations(),
      });

      const command = editorStore!.editor.commandAddMediaElement(
        imageElement,
        `${track}`,
        TimelineMode.OVERLAP, // Use OVERLAP mode to allow adjacent photos on same track
      );

      if (!command) {
        throw new Error('Failed to create image element via headless editor');
      }
    }
  }

  // TODO: Move to Repository pattern whenever we move Story
  async attachPunchListPhotosToStory(): Promise<StoryDTO> {
    const storyDTO: StoryDTO = { ...this.videoCreator.story! };
    const srcUrls = this.editorStore.allElementStates
      .map(e => (e.source.type === 'image' ? e.source.source : undefined))
      .filter(Boolean);
    try {
      if (
        this.videoCreator.photoDataForDato &&
        Object.values(this.videoCreator.photoDataForDato)
      ) {
        for (let [id, blobData] of Object.entries(
          this.videoCreator.photoDataForDato,
        )) {
          if (!srcUrls?.includes(blobData.url)) {
            continue;
          }

          const idx = this.punchListData?.findIndex(p => p.id === id);
          if (idx && idx > -1) {
            if (!this.punchListData?.length) continue;

            if ('uploadId' in blobData && blobData.uploadId) {
              storyDTO.aiPhotos?.push({ id: blobData.uploadId });
              continue;
            }

            const line = this.punchListData[idx].line;
            const fileName = line
              .split(' ')
              .join('_')
              .toLowerCase()
              .slice(0, 30);

            const newPhotoData = {
              ...blobData,
              fileName,
              alt: line,
              title: line,
              metaData: {
                alt: line,
                title: line,
                custom_data: {},
              },
            };

            const newUpload =
              await this.datoClientStore.assetRepository?.uploadFile(
                newPhotoData,
              );

            this.punchListData[idx] = {
              ...this.punchListData[idx],
              artifactSrc: newUpload!.url,
              artifactAspectRatio:
                (newUpload!.width || 0) / (newUpload!.height || 1),
            };

            if (blobData.type === 'ai') {
              storyDTO.aiPhotos?.push(newUpload!);
            } else if (blobData.type === 'stock') {
              storyDTO.storyAssets?.push(newUpload!);
            }
          } else {
            if (!('fileName' in blobData)) continue;

            const newPhotoData = {
              ...blobData,
              alt: blobData.alt || '',
              title: blobData.title || '',
            };

            const newUpload =
              await this.datoClientStore.assetRepository?.uploadFile(
                newPhotoData,
              );

            if (blobData.type === 'stock') {
              storyDTO.storyAssets?.push(newUpload!);
            } else if (blobData.type === 'ai') {
              storyDTO.aiPhotos?.push(newUpload!);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error attaching punch list photos to story: ', error);
      console.log('returning unmodified storyDTO', storyDTO);
    }

    return storyDTO;
  }

  insertPunchListAtIndex = (
    newPunchListData: PunchListItem[],
    punchListData = this.punchListData,
  ) => {
    const sortedPunchList = newPunchListData.sort(
      (a, b) => a.transcriptPosition.endIndex - b.transcriptPosition.endIndex,
    );
    const lastPunchList = sortedPunchList[sortedPunchList.length - 1];
    const endIndex = lastPunchList.transcriptPosition.endIndex;

    const punchListLength = punchListData!.length;
    if (
      !punchListLength ||
      endIndex > punchListData![punchListLength - 1].transcriptPosition.endIndex
    ) {
      const existingPunchListData = punchListData || [];
      punchListData = [...existingPunchListData, ...sortedPunchList];
    } else {
      const indexToInsert = punchListData?.findIndex(
        p => p.transcriptPosition.endIndex > endIndex,
      );
      const punchListBefore = punchListData?.slice(0, indexToInsert!) || [];
      const punchListAfter = punchListData?.slice(indexToInsert!) || [];
      punchListData = [
        ...punchListBefore,
        ...sortedPunchList,
        ...punchListAfter,
      ];
    }
    return punchListData;
  };

  public isEmptyItem(punchListItem: PunchListItem): boolean {
    return !punchListItem.artifactSrc;
  }

  public isAiGeneratedItem(punchListItem: PunchListItem): boolean {
    return punchListItem.sub_type !== 'manual';
  }

  private getPunchListForMatching(
    mode: 'replace-missing' | 'replace-all',
  ): PunchListItem[] {
    let result = this.punchListItems.filter(this.isAiGeneratedItem);
    if (mode === 'replace-missing') {
      result = result.filter(this.isEmptyItem);
    }
    return result;
  }

  public async matchWithSources(
    sources: string[],
    mode: 'replace-missing' | 'replace-all',
  ): Promise<void> {
    const gptService = new ChatGPTService(
      this.videoCreator,
      this.datoClientStore.aiPromptRepository,
    );
    const response = await gptService.matchPunchListWithSources(
      this.getPunchListForMatching(mode),
      sources,
    );
    if (response.punchList) {
      await this.applyPunchListUpdates(response.punchList);
    }
  }

  findOrReplaceInTimeline = async (
    id: string,
    _source: string,
    time: number | null = null,
    artifactAspectRatio: number | null = null,
  ) => {
    // Tried to simplify logic to migrate - instead of modiying if element exists, just remove and create new image
    const videoAspectRatio = this.videoCreator.getCurrentAspectRatio();
    const animations = [
      {
        start_scale: '100%',
        end_scale: DEFAULT_ANIMATION_SPEED,
        x_anchor: '50%',
        y_anchor: '50%',
        fade: false,
        scope: 'element',
        easing: 'linear',
        type: 'scale',
        arbor_subType: 'zoomIn',
      },
    ];

    const element = this.editorStore.editor.getElementById(id);
    const isPortrait = !!artifactAspectRatio && artifactAspectRatio < 1;
    const source = applyImageOptimizations(_source);

    const timeToUse = element?.time ?? time;
    const duration = element?.durationSeconds || 8;

    const freeTrack =
      element?.track ??
      this.videoCreator.getFreeMediaTrack(
        'image',
        duration,
        timeToUse ?? undefined,
      );

    const trackToUse = element?.track ?? freeTrack;

    const newImageProps =
      isPortrait && videoAspectRatio === AspectRatio.AR_16_9
        ? {
            id,
            type: 'composition',
            duration: `${duration} s`,
            elements: getImageWithBlackFrameElements({ source }),
            ...(timeToUse && { time: timeToUse }),
            ...(trackToUse && { track: trackToUse }),
            animations,
          }
        : {
            id,
            type: 'image',
            source,
            duration: `${duration} s`,
            autoplay: true,
            fit: 'cover',
            smart_crop: true,
            ...(timeToUse && { time: timeToUse }),
            ...(trackToUse && { track: trackToUse }),
            animations,
          };

    const imageElement = Timeline.createElement(
      newImageProps.type,
      newImageProps,
    );
    let removed = !element;
    if (element) {
      removed = this.editorStore.editor.commandRemoveElement(element.id);
    }
    if (removed) {
      this.editorStore.editor.commandAddMediaElement(
        imageElement,
        freeTrack?.toString(),
        TimelineMode.OVERLAP,
      );
    }

    setTimeout(async () => {
      await this.videoCreator.setActiveElements(imageElement.id);
    }, 300);
  };

  private async applyPunchListUpdates(updatedPunchListItems: PunchListItem[]) {
    const validUpdatedItems = updatedPunchListItems.filter(
      item => item.line && item.line.trim() !== '',
    );
    const newPunchList: PunchListItem[] = [];
    for (let punchListItem of this.punchListItems) {
      const updatedPunchListItem = validUpdatedItems.find(
        item => item.id === punchListItem.id,
      );
      if (!updatedPunchListItem) {
        newPunchList.push(punchListItem);
        continue;
      }
      newPunchList.push(updatedPunchListItem);
      if (punchListItem.artifactSrc !== updatedPunchListItem.artifactSrc) {
        await this.findOrReplaceInTimeline(
          updatedPunchListItem.id!,
          updatedPunchListItem.artifactSrc || '',
          updatedPunchListItem.startTime,
          updatedPunchListItem.artifactAspectRatio,
        );
      }
    }
    runInAction(() => {
      this.punchListData = newPunchList;
    });
  }

  /**
   * Creates a MobX reaction that syncs punch list items to editor when they change.
   * Similar to onExtraElementDataChange pattern.
   * Call this in VideoCreatorStore constructor to set up automatic sync.
   */
  onPunchListItemsChange(cb: (items: PunchListItem[]) => void) {
    return reaction(
      () => this.punchListData,
      items => {
        if (!this.isSyncingFromEditor) {
          cb(items ?? []);
        }
      },
      { fireImmediately: false },
    );
  }
}

export default PunchListManager;
